import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  avatarApi,
  useFeatureShopItemMutation,
  useGetFeaturedAvatarQuery,
  useGetShopItemsQuery,
  useGetShopOwnedQuery,
  useRedeemShopItemMutation,
} from '@/services/avatarApi';
import { RichiedeAccesso } from '@/components/common/RichiedeAccesso';
import { Button } from '@/components/common/Button';
import { useHaptics } from '@/hooks/useHaptics';
import { useOspite } from '@/hooks/useOspite';
import { useAppDispatch } from '@/store/hooks';
import { codiceNegozio, inVetrina, soloGratuiti, type VoceNegozio } from '@/lib/avatarShop';
import { statoHttp } from '@/lib/avatarEmotes';

/**
 * Il NEGOZIO degli avatar, SOLO il riscatto gratuito (I-612, Luca 11/10 «1 (a)»). SOLO col flag `featureAvatarShopEnabled` (la rotta
 * non esiste a flag spento).
 *
 * ⛔ Niente prezzi, niente Essenze, niente pagamento: un articolo con un prezzo non si mostra (`libero` = `price === null`). Il testo
 * di un articolo (titolo, descrizione) e' del server e si mette come TESTO, mai HTML; il `message` di un errore non si mostra mai.
 * ⚠️ `verified: false` su `/owned` e' «non so dirlo»: si dice, mai «nessun oggetto». `owned: null` su una voce non e' `false`.
 */
type Frase = 'gone' | 'off' | 'redeemFailed' | 'featureNotOwned' | 'featureUnverified' | 'featureFailed';
/** Un rifiuto: la frase e, per «non riesco a verificare», i secondi di `Retry-After` (assente = «fra poco»). */
type Rifiuto = { frase: Frase; secondi?: number };

/** I codici con cui Qavatar dice «questo articolo non c'e' piu'/non e' piu' gratuito». Un 404/409 SENZA uno di questi non e' «non piu' disponibile». */
const NON_DISPONIBILE = new Set(['item_not_found', 'item_not_available', 'item_not_free', 'item_unavailable', 'item_id_invalid']);

/**
 * Un errore di riscatto/scelta → la frase: ognuna dice cosa e' successo, mai il testo del server.
 * ⚠️ Il codice sta in `code` sulla PATCH feature e in `error` sul redeem (QAvatar `feat/QAvatar`): `codiceNegozio` legge quello giusto.
 * ⚠️ Un 503 SENZA codice noto NON e' «negozio spento» (puo' essere un guasto qualunque): e' «errore, riprova».
 */
function rifiutoDi(errore: unknown, azione: 'redeem' | 'feature'): Rifiuto {
  const stato = statoHttp(errore);
  const codice = codiceNegozio(errore, azione === 'feature' ? 'code' : 'error');
  if (stato === 503 && codice === 'item_ownership_unverified') {
    return { frase: 'featureUnverified', secondi: (errore as { retryAfterS?: number } | null)?.retryAfterS };
  }
  if (stato === 503 && codice === 'shop_not_configured') return { frase: 'off' };
  if (stato === 403 && codice === 'item_not_owned') return { frase: 'featureNotOwned' };
  if ((stato === 404 || stato === 409 || stato === 400) && codice && NON_DISPONIBILE.has(codice)) return { frase: 'gone' };
  return { frase: azione === 'redeem' ? 'redeemFailed' : 'featureFailed' };
}

export function AvatarNegozio() {
  const ospite = useOspite();
  // Da ospite `guardiaOspite` blocca gia' Qavatar: qui non si monta nemmeno una query.
  if (ospite) {
    return (
      <div data-testid="avatar-negozio" data-stato="ospite">
        <RichiedeAccesso />
      </div>
    );
  }
  return <NegozioAperto />;
}

function NegozioAperto() {
  const { t } = useTranslation('avatars');
  const haptics = useHaptics();
  const dispatch = useAppDispatch();
  const items = useGetShopItemsQuery();
  const owned = useGetShopOwnedQuery();
  const featured = useGetFeaturedAvatarQuery();
  const [riscatta] = useRedeemShopItemMutation();
  const [metti] = useFeatureShopItemMutation();

  const [inCorso, setInCorso] = useState<string | null>(null);
  const [frasi, setFrasi] = useState<Record<string, Rifiuto>>({});
  // Gli articoli appena riscattati: «Ce l'hai» subito, senza aspettare che le due liste si rileggano.
  const [riscattati, setRiscattati] = useState<ReadonlySet<string>>(new Set());

  const setFrase = useCallback((itemId: string, f: Rifiuto | null) => {
    setFrasi((prima) => {
      const resto = { ...prima };
      delete resto[itemId];
      return f ? { ...resto, [itemId]: f } : resto;
    });
  }, []);

  const onRiscatta = useCallback(
    async (itemId: string) => {
      haptics.impact('light');
      setInCorso(itemId);
      setFrase(itemId, null);
      try {
        await riscatta(itemId).unwrap(); // 201 granted e 200 already_granted: tutti e due «Ce l'hai»
        setRiscattati((s) => new Set(s).add(itemId));
      } catch (e) {
        setFrase(itemId, rifiutoDi(e, 'redeem'));
      } finally {
        setInCorso(null);
      }
    },
    [haptics, riscatta, setFrase],
  );

  const onVetrina = useCallback(
    async (itemId: string) => {
      haptics.impact('light');
      setInCorso(itemId);
      setFrase(itemId, null);
      try {
        const data = await metti(itemId).unwrap();
        // Lo STESSO `data` di GET /avatars/featured: la cache si aggiorna da qui, senza rileggere.
        await dispatch(avatarApi.util.upsertQueryData('getFeaturedAvatar', undefined, data)).unwrap();
      } catch (e) {
        setFrase(itemId, rifiutoDi(e, 'feature'));
      } finally {
        setInCorso(null);
      }
    },
    [haptics, metti, dispatch, setFrase],
  );

  const gratuiti = items.data ? soloGratuiti(items.data.items) : [];
  // «Errore» solo SENZA dati: una rilettura fallita (dopo un riscatto, per dire) non cancella cio' che si e' gia' letto.
  const stato = items.isLoading
    ? 'caricamento'
    : !items.data
      ? 'errore'
      : !items.data.enabled
        ? 'spento'
        : gratuiti.length === 0
          ? 'vuoto'
          : 'pronto';

  // `dove`: 'elenco' = i gratuiti da /items; 'miei' = /owned, dove ogni voce e' posseduta per definizione (anche un ritirato).
  const riga = (v: VoceNegozio, dove: 'elenco' | 'miei') => {
    const possiedo = dove === 'miei' || v.owned === true || riscattati.has(v.itemId);
    const inVetr = inVetrina(featured.data, v);
    const occupato = inCorso === v.itemId;
    const frase = frasi[v.itemId];
    const img = v.thumbnail ?? v.image;
    return (
      <li key={v.itemId} data-testid={`negozio-voce-${v.itemId}`} className="flex flex-col gap-2 rounded-xl bg-bg-secondary p-3">
        <div className="flex items-start gap-3">
          <span className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-black">
            {img && <img src={img} alt="" className="h-full w-full object-cover" loading="lazy" />}
          </span>
          <div className="flex min-w-0 flex-1 flex-col">
            <span className="break-words text-sm font-bold text-fg">{v.title}</span>
            {v.description && <span className="line-clamp-2 break-words text-[11px] text-fg-muted">{v.description}</span>}
          </div>
        </div>
        {possiedo ? (
          <div className="flex items-center justify-between gap-2">
            <span data-testid={`negozio-ce-lhai-${v.itemId}`} className="text-sm font-semibold text-success">
              {t('shop.owned')}
            </span>
            {inVetr ? (
              <span data-testid={`negozio-in-vetrina-${v.itemId}`} className="text-sm font-semibold text-brand">
                {t('shop.featured')}
              </span>
            ) : (
              <Button size="sm" data-testid={`negozio-vetrina-${v.itemId}`} disabled={occupato} onClick={() => void onVetrina(v.itemId)}>
                {occupato ? t('shop.featuring') : t('shop.feature')}
              </Button>
            )}
          </div>
        ) : (
          <Button fullWidth size="sm" data-testid={`negozio-riscatta-${v.itemId}`} disabled={occupato} onClick={() => void onRiscatta(v.itemId)}>
            {occupato ? t('shop.redeeming') : frase?.frase === 'redeemFailed' ? t('shop.retry') : t('shop.redeem')}
          </Button>
        )}
        {frase && (
          <p role="alert" data-testid={`negozio-frase-${v.itemId}`} className="text-[11px] text-danger">
            {frase.frase === 'featureUnverified' && frase.secondi ? t('shop.featureUnverifiedIn', { n: frase.secondi }) : t(`shop.${frase.frase}`)}
          </p>
        )}
      </li>
    );
  };

  const mieiOggetti = () => {
    if (owned.isLoading) {
      return <div data-testid="negozio-miei-caricamento" className="h-14 animate-pulse rounded-xl bg-bg-secondary" />;
    }
    if (!owned.data) {
      return (
        <div role="alert" data-testid="negozio-miei-errore" className="flex flex-col items-center gap-2 rounded-xl bg-bg-secondary p-4 text-center text-sm text-danger">
          <span>{t('shop.mineFailed')}</span>
          <Button size="sm" onClick={() => void owned.refetch()}>
            {t('shop.retry')}
          </Button>
        </div>
      );
    }
    if (!owned.data.enabled) {
      // Il negozio e' spento anche per «I miei oggetti»: lo si dice, non si lascia una sezione vuota.
      return (
        <div role="status" data-testid="negozio-miei-spento" className="rounded-xl bg-bg-secondary p-4 text-center text-sm text-fg-muted">
          {t('shop.off')}
        </div>
      );
    }
    if (!owned.data.verified) {
      // «Non so dirlo»: mai «nessun oggetto».
      return (
        <div role="status" data-testid="negozio-miei-non-verificati" className="flex flex-col items-center gap-2 rounded-xl bg-bg-secondary p-4 text-center text-sm text-fg-muted">
          <span>{t('shop.mineUnverified')}</span>
          <Button size="sm" onClick={() => void owned.refetch()}>
            {t('shop.retry')}
          </Button>
        </div>
      );
    }
    if (owned.data.items.length === 0) {
      return (
        <p data-testid="negozio-miei-vuoto" className="rounded-xl bg-bg-secondary p-4 text-center text-sm text-fg-muted">
          {t('shop.mineEmpty')}
        </p>
      );
    }
    return <ul className="flex flex-col gap-2">{owned.data.items.map((v) => riga(v, 'miei'))}</ul>;
  };

  return (
    <div className="flex flex-col gap-4 px-4 pb-24 pt-5" data-testid="avatar-negozio" data-stato={stato}>
      <header>
        {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. */}
        <h1 className="sr-only">{t('shop.title')}</h1>
        <p className="text-sm text-fg-muted">{t('shop.intro')}</p>
      </header>

      {stato === 'caricamento' && <div data-testid="negozio-caricamento" className="h-24 animate-pulse rounded-xl bg-bg-secondary" />}
      {stato === 'errore' && (
        <div role="alert" data-testid="negozio-errore" className="flex flex-col items-center gap-2 rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
          <span>{t('shop.loadFailed')}</span>
          <Button size="sm" onClick={() => void items.refetch()}>
            {t('shop.retry')}
          </Button>
        </div>
      )}
      {stato === 'spento' && (
        <div role="status" data-testid="negozio-spento" className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          {t('shop.off')}
        </div>
      )}
      {stato === 'vuoto' && (
        <div role="status" data-testid="negozio-vuoto" className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          {t('shop.empty')}
        </div>
      )}
      {stato === 'pronto' && (
        <section aria-labelledby="negozio-gratis">
          <h2 id="negozio-gratis" className="mb-2 text-sm font-semibold text-fg">
            {t('shop.free')}
          </h2>
          <ul className="flex flex-col gap-2">{gratuiti.map((v) => riga(v, 'elenco'))}</ul>
        </section>
      )}

      {(stato === 'pronto' || stato === 'vuoto') && (
        <section aria-labelledby="negozio-miei">
          <h2 id="negozio-miei" className="mb-2 text-sm font-semibold text-fg">
            {t('shop.mine')}
          </h2>
          {mieiOggetti()}
        </section>
      )}
    </div>
  );
}
